"use client";

import { useCallback } from "react";
import { ensureCsrfToken } from "@/lib/csrf-client";
import type { WithCsrf } from "@/lib/withSecurity";

/**
 * Wraps a security-protected server action to auto-inject the CSRF token.
 *
 * Without this hook, every call site has to do:
 * ```ts
 * const csrfToken = await ensureCsrfToken();
 * await someAction({ ...payload, csrfToken });
 * ```
 *
 * With this hook:
 * ```ts
 * const someAction = useServerAction(originalAction);
 * await someAction(payload);   // csrfToken auto-injected
 * ```
 *
 * The wrapped function takes the same input as the action MINUS the
 * `csrfToken` field. Actions that take only `{ csrfToken }` (like logout)
 * become callable as `await wrapped()` with no arguments.
 */
export function useServerAction<TFullInput extends WithCsrf, TResult>(
  action: (input: TFullInput) => Promise<TResult>,
) {
  type ClientInput = Omit<TFullInput, "csrfToken">;

  return useCallback(
    async (input: ClientInput = {} as ClientInput): Promise<TResult> => {
      const csrfToken = await ensureCsrfToken();
      return action({ ...input, csrfToken } as TFullInput);
    },
    [action],
  );
}
